import { useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { deleteElement, getElement, publishElement, updateElement } from '../../../api/elements.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

function formatDate(value) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function ElementDetailPage() {
  const { applicationId } = useOutletContext()
  const { confirm } = useFeedback()
  const { elementId } = useParams()
  const navigate = useNavigate()
  const [pageState, setPageState] = useState({
    status: 'loading',
    element: null,
    error: null,
  })
  const [groupConfigs, setGroupConfigs] = useState([])
  const [values, setValues] = useState({
    name: '',
    groupConfigId: '',
    elementType: 'advertising',
    jsonConfig: '',
  })
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    listGroupConfigs(applicationId)
      .then((list) => {
        if (!cancelled) setGroupConfigs(list || [])
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [applicationId])

  useEffect(() => {
    let cancelled = false

    setPageState({
      status: 'loading',
      element: null,
      error: null,
    })

    getElement(applicationId, elementId)
      .then((element) => {
        if (cancelled) return
        setPageState({
          status: 'ok',
          element,
          error: null,
        })
        setValues({
          name: element?.name || '',
          groupConfigId: element?.groupConfigId != null ? String(element.groupConfigId) : '',
          elementType: element?.elementType || 'advertising',
          jsonConfig:
            element?.rawJsonConfig ||
            (element?.jsonConfig ? JSON.stringify(element.jsonConfig, null, 2) : ''),
        })
      })
      .catch((requestError) => {
        if (cancelled) return
        setPageState({
          status: 'error',
          element: null,
          error: requestError,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, elementId])

  const element = pageState.element

  if (!element) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">{pageState.status === 'loading' ? 'Loading element...' : 'Element not found'}</div>
        {pageState.status === 'error' ? (
          <pre className="codeBlock codeBlockError">{String(pageState.error?.message || pageState.error)}</pre>
        ) : null}
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/elements`} className="button buttonGhost">
            Back to elements
          </Link>
        </div>
      </section>
    )
  }

  function onChange(field) {
    return (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))
  }

  async function handleSave() {
    setError(null)
    const name = String(values.name || '').trim()
    if (!name) {
      setError(new Error('Name is required'))
      return
    }
    try {
      const updatedElement = await updateElement(applicationId, element.id, {
        name,
        groupConfigId: values.groupConfigId || undefined,
        elementType: values.elementType,
        jsonConfig: values.jsonConfig,
      })
      setPageState({
        status: 'ok',
        element: updatedElement,
        error: null,
      })
    } catch (e) {
      setError(e)
    }
  }

  async function handlePublish() {
    setError(null)
    try {
      const updatedElement = await publishElement(applicationId, element.id)
      setPageState({
        status: 'ok',
        element: updatedElement,
        error: null,
      })
    } catch (e) {
      setError(e)
    }
  }

  async function handleDelete() {
    setError(null)
    const confirmed = await confirm({
      title: 'Delete element',
      message: 'Delete this element? This action cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteElement(applicationId, element.id)
      navigate(`/applications/${applicationId}/elements`)
    } catch (e) {
      setError(e)
    }
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Element: {element.name || 'Unnamed'}</h1>
          <div className="muted">
            Status: <strong>{element.status}</strong> • Updated: {formatDate(element.updatedAt || element.createdAt)}
          </div>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/elements`} className="button buttonGhost">
            Back
          </Link>
          {element.status !== 'published' && element.status !== 'archived' ? (
            <button type="button" className="button buttonPrimary" onClick={handlePublish}>
              Publish
            </button>
          ) : null}
          {element.status !== 'published' ? (
            <button type="button" className="button buttonDanger" onClick={handleDelete} disabled>
              Delete
            </button>
          ) : null}
          <button type="button" className="button buttonPrimary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>

      {error ? <div className="formError">{String(error?.message || error)}</div> : null}

      <section className="card formCard">
        <div className="cardTitle">Edit</div>
        <div className="formGrid">
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} />
          </label>
          <label className="formField">
            <span className="formLabel">Group config (optional)</span>
            <select className="textInput" value={String(values.groupConfigId || '')} onChange={onChange('groupConfigId')}>
              <option value="">— None —</option>
              {groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">Type</span>
            <select className="textInput" value={values.elementType} onChange={onChange('elementType')}>
              <option value="advertising">advertising</option>
              <option value="inapp">inapp</option>
              <option value="balance">balance</option>
            </select>
          </label>
          <label className="formField">
            <span className="formLabel">JSON config</span>
            <textarea
              className="textInput textAreaMonoLg"
              value={values.jsonConfig}
              onChange={onChange('jsonConfig')}
            />
          </label>
        </div>
      </section>
    </div>
  )
}

